import React, { useState, useEffect } from 'react';
import Header from '../Header/Header'
import HeaderDash from '../../Components/Admin/HeaderDash/HeaderDash'
import SinPermisos from '../../Components/SinPermisos/SinPermisos';
import { fetchUsuario, getUsuario } from '../../Components/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable, faColumns, faFilter, faDownload, faEye, faPlus, faTrash, faCog } from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../Components/url';
import './Reportes.css';
import { puedeVerMenu } from '../../Helpers/rolesPermisos';

export default function Reportes() {
    const [loading, setLoading] = useState(true);
    const [selectedTables, setSelectedTables] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState({});
    const [filters, setFilters] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [activeStep, setActiveStep] = useState(1);

    // Definir las tablas disponibles con sus columnas
    const availableTables = {
        'turnos': {
            name: 'Citas',
            description: 'Información de citas y turnos programados',
            columns: {
                'idTurno': 'ID Turno',
                'fecha_cita': 'Fecha de Cita',
                'hora_inicio': 'Hora Inicio',
                'hora_fin': 'Hora Fin',
                'nombre': 'Nombre del Paciente',
                'telefono': 'Teléfono',
                'categoria': 'Categoría/Centro',
                'servicio': 'Servicio',
                'poblacion': 'Población',
                'tipo': 'Tipo',
                'terapeuta': 'Terapeuta Asignado',
                'sala': 'Sala Asignada',
                'periodicidad': 'Periodicidad',
                'estado': 'Estado'
            }
        },
        'usuarios': {
            name: 'Usuarios/Terapeutas',
            description: 'Información de usuarios y terapeutas del sistema',
            columns: {
                'idUsuario': 'ID Usuario',
                'nombre': 'Nombre',
                'email': 'Email',
                'rol': 'Rol',
                'telefono': 'Teléfono',
                'estatus': 'Estatus',
                'createdAt': 'Fecha de Registro'
            }
        },
        'servicios': {
            name: 'Servicios/Programas',
            description: 'Servicios y programas disponibles',
            columns: {
                'idServicio': 'ID Servicio',
                'titulo': 'Título',
                'descripcion': 'Descripción',
                'precio': 'Precio',
                'poblacion': 'Población Objetivo'
            }
        },
        'categorias': {
            name: 'Centros CESECO',
            description: 'Centros de servicio',
            columns: {
                'idCategoria': 'ID Categoría',
                'categoria': 'Nombre del Centro',
                'direccion': 'Dirección',
                'telefono': 'Teléfono',
                'email': 'Email',
                'ciudad': 'Ciudad',
                'instagram': 'Instagram',
                'facebook': 'Facebook',
                'mapaUrl': 'URL de Mapa'
            }
        },
        'pacientes': {
            name: 'Pacientes',
            description: 'Información demográfica de pacientes',
            columns: {
                'id': 'ID Paciente',
                'nombre': 'Nombre',
                'sexo': 'Sexo',
                'escolaridad': 'Escolaridad',
                'estadoCivil': 'Estado Civil',
                'ocupacion': 'Ocupación',
                'servicioSalud': 'Servicio de Salud',
                'religion': 'Religión',
                'municipio': 'Municipio',
                'motivoConsulta': 'Motivo de Consulta',
                'comoEntero': '¿Cómo se enteró?'
            }
        },
        'notifications': {
            name: 'Notificaciones',
            description: 'Historial de notificaciones enviadas',
            columns: {
                'id': 'ID Notificación',
                'sendTo': 'Destinatario',
                'templateName': 'Plantilla',
                'status': 'Estado',
                'createdDate': 'Fecha de Creación',
                'dateSent': 'Fecha de Envío'
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchUsuario();
            setLoading(false);
        };
        fetchData();
    }, []);

    const usuarioLegued = getUsuario();

    const handleTableSelect = (tableKey) => {
        setSelectedTables([tableKey]);
        setSelectedColumns({}); // Limpiar columnas seleccionadas al cambiar de tabla
    };

    const handleColumnToggle = (tableKey, columnKey) => {
        const currentColumns = selectedColumns[tableKey] || [];
        if (currentColumns.includes(columnKey)) {
            const newColumns = currentColumns.filter(c => c !== columnKey);
            setSelectedColumns({
                ...selectedColumns,
                [tableKey]: newColumns
            });
        } else {
            setSelectedColumns({
                ...selectedColumns,
                [tableKey]: [...currentColumns, columnKey]
            });
        }
    };

    const addFilter = () => {
        setFilters([...filters, {
            id: Date.now(),
            table: '',
            column: '',
            operator: '=',
            value: ''
        }]);
    };

    const removeFilter = (filterId) => {
        setFilters(filters.filter(f => f.id !== filterId));
    };

    const updateFilter = (filterId, field, value) => {
        setFilters(filters.map(f => 
            f.id === filterId ? { ...f, [field]: value } : f
        ));
    };

    const generateReport = async () => {
        setIsGenerating(true);
        
        try {
            const reportConfig = {
                selectedTables: selectedTables,
                selectedColumns: selectedColumns,
                filters: filters
            };

            const response = await fetch(`${baseURL}/reportesGet.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reportConfig)
            });

            const result = await response.json();

            if (result.success) {
                // Combinar todos los datos de las tablas en una sola lista
                const allData = [];
                Object.values(result.data).forEach(tableData => {
                    if (Array.isArray(tableData)) {
                        allData.push(...tableData);
                    }
                });
                
                setReportData(allData);
                setIsGenerating(false);
                setActiveStep(3);
            } else {
                console.error('Error al generar reporte:', result.error);
                setIsGenerating(false);
                // Aquí podrías mostrar un mensaje de error al usuario
            }
        } catch (error) {
            console.error('Error en la petición:', error);
            setIsGenerating(false);
            // Aquí podrías mostrar un mensaje de error al usuario
        }
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        // Nombre base del reporte
        const reportName = 'reporte_'+ (availableTables[mainTable]?.name || mainTable || 'reporte').replace(/\s+/g, '_');
        // Filtros aplicados
        const appliedFilters = filters.filter(f => f.table === mainTable && f.column && f.value);
        let filterString = '';
        if (appliedFilters.length > 0) {
            filterString = appliedFilters.map(f => `${f.column}_${String(f.value).replace(/\s+/g, '_')}`).join('_');
        } else {
            filterString = 'sin_filtros';
        }
        const fileName = `${reportName}${filterString ? '_' + filterString : ''}.csv`;
        const csvRows = [];
        // Encabezados
        csvRows.push(orderedColumnKeys.map(key => `"${availableTables[mainTable]?.columns[key] || key}"`).join(','));
        // Filas de datos
        reportData.forEach(row => {
            csvRows.push(orderedColumnKeys.map(key => `"${row[key] !== undefined && row[key] !== null ? String(row[key]).replace(/"/g, '""') : ''}"`).join(','));
        });
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Obtener el orden lógico de columnas según el objeto columns de la tabla principal
    const mainTable = selectedTables[0];
    const logicalColumnOrder = mainTable && availableTables[mainTable]
        ? Object.keys(availableTables[mainTable].columns)
        : reportData.length > 0 ? Object.keys(reportData[0]) : [];
    // Filtrar solo las seleccionadas, pero en el orden lógico
    const orderedColumnKeys = logicalColumnOrder.filter(key => selectedColumns[mainTable]?.includes(key));

    if (loading) {
        return <div>Cargando...</div>;
    }

    if (!usuarioLegued?.idUsuario || !puedeVerMenu(usuarioLegued.rol, 'reportes')) {
        return <SinPermisos />;
    }

    return (
        <div className='containerGrid'>
            <Header />
            <section className='containerSection'>
                <HeaderDash />
                <div className='container'>
                    <div className="reportes-container">
                        <div className="reportes-header">
                            <h2><FontAwesomeIcon icon={faCog} /> Generador de Reportes</h2>
                            <p>Configura y genera reportes personalizados de tu sistema CESECO</p>
                        </div>

                        <div className="reportes-steps">
                            <div className={`step ${activeStep >= 1 ? 'active' : ''}`}>
                                <span className="step-number">1</span>
                                <span className="step-title">Seleccionar Tablas</span>
                            </div>
                            <div className={`step ${activeStep >= 2 ? 'active' : ''}`}>
                                <span className="step-number">2</span>
                                <span className="step-title">Configurar Columnas</span>
                            </div>
                            <div className={`step ${activeStep >= 3 ? 'active' : ''}`}>
                                <span className="step-number">3</span>
                                <span className="step-title">Resultados</span>
                            </div>
                        </div>

                        {activeStep === 1 && (
                            <div className="step-content">
                                <div className="tables-selection">
                                    <h3><FontAwesomeIcon icon={faTable} /> Selecciona el tipo de reporte</h3>
                                    <div className="tables-grid">
                                        {Object.entries(availableTables).map(([key, table]) => (
                                            <div 
                                                key={key}
                                                className={`table-card ${selectedTables.includes(key) ? 'selected' : ''}`}
                                                onClick={() => handleTableSelect(key)}
                                            >
                                                <div className="table-card-header">
                                                    <h4>{table.name}</h4>
                                                    <div className="table-checkbox">
                                                        <input 
                                                            type="radio" 
                                                            checked={selectedTables.includes(key)}
                                                            onChange={() => handleTableSelect(key)}
                                                        />
                                                    </div>
                                                </div>
                                                <p>{table.description}</p>
                                                <div className="table-stats">
                                                    <span>{Object.keys(table.columns).length} columnas disponibles</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button 
                                        className="btn-next"
                                        onClick={() => setActiveStep(2)}
                                        disabled={selectedTables.length === 0}
                                    >
                                        Continuar
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeStep === 2 && (
                            <div className="step-content">
                                <div className="columns-configuration">
                                    <h3><FontAwesomeIcon icon={faColumns} /> Configura las columnas de tu reporte</h3>
                                    
                                    {selectedTables.map(tableKey => (
                                        <div key={tableKey} className="table-columns">
                                            <h4>{availableTables[tableKey].name}</h4>
                                            <div className="columns-grid">
                                                {Object.entries(availableTables[tableKey].columns).map(([columnKey, columnName]) => (
                                                    <div 
                                                        key={columnKey}
                                                        className={`column-item ${(selectedColumns[tableKey] || []).includes(columnKey) ? 'selected' : ''}`}
                                                        onClick={() => handleColumnToggle(tableKey, columnKey)}
                                                    >
                                                        <input 
                                                            type="checkbox" 
                                                            checked={(selectedColumns[tableKey] || []).includes(columnKey)}
                                                            onChange={() => {}}
                                                        />
                                                        <span>{columnName}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    <div className="filters-section">
                                        <h4><FontAwesomeIcon icon={faFilter} /> Filtros (Opcional)</h4>
                                        {filters.map(filter => (
                                            <div key={filter.id} className="filter-row">
                                                <select 
                                                    value={filter.table}
                                                    onChange={(e) => updateFilter(filter.id, 'table', e.target.value)}
                                                >
                                                    <option value="">Seleccionar tabla</option>
                                                    {selectedTables.map(tableKey => (
                                                        <option key={tableKey} value={tableKey}>
                                                            {availableTables[tableKey].name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select 
                                                    value={filter.column}
                                                    onChange={(e) => updateFilter(filter.id, 'column', e.target.value)}
                                                >
                                                    <option value="">Seleccionar columna</option>
                                                    {filter.table && availableTables[filter.table] && 
                                                        Object.entries(availableTables[filter.table].columns).map(([key, name]) => (
                                                            <option key={key} value={key}>{name}</option>
                                                        ))
                                                    }
                                                </select>
                                                <select 
                                                    value={filter.operator}
                                                    onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                                                >
                                                    <option value="=">=</option>
                                                    <option value="!=">≠</option>
                                                    <option value=">">&gt;</option>
                                                    <option value="<">&lt;</option>
                                                    <option value="LIKE">Contiene</option>
                                                </select>
                                                <input 
                                                    type="text"
                                                    placeholder="Valor"
                                                    value={filter.value}
                                                    onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                                />
                                                <button 
                                                    className="btn-remove"
                                                    onClick={() => removeFilter(filter.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        ))}
                                        <button className="btn-add-filter" onClick={addFilter}>
                                            <FontAwesomeIcon icon={faPlus} /> Agregar Filtro
                                        </button>
                                    </div>

                                    <div className="step-actions">
                                        <button className="btn-back" onClick={() => setActiveStep(1)}>
                                            Atrás
                                        </button>
                                        <button 
                                            className="btn-generate"
                                            onClick={generateReport}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? 'Generando...' : 'Generar Reporte'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeStep === 3 && (
                            <div className="step-content">
                                <div className="report-results">
                                    <div className="results-header">
                                        <h3><FontAwesomeIcon icon={faEye} /> Resultados del Reporte</h3>
                                        <div className="export-actions">
                                            <button className="btn-export" onClick={exportToCSV}>
                                                <FontAwesomeIcon icon={faDownload} /> Exportar CSV
                                            </button>
                                        </div>
                                    </div>
                                    {/* Subtítulo con el nombre del reporte y filtros aplicados */}
                                    {mainTable && (
                                        <div className="report-subtitle" style={{marginBottom: '1rem'}}>
                                            <strong>Reporte:</strong> {availableTables[mainTable]?.name || mainTable}
                                            {filters.length > 0 && (
                                                <div style={{marginTop: '0.5rem', fontSize: '0.95em', color: 'var(--text-color2)'}}>
                                                    <strong>Filtros aplicados:</strong>
                                                    <ul style={{margin: 0, paddingLeft: '1.2em'}}>
                                                        {filters.filter(f => f.table === mainTable && f.column && f.value).map((f, idx) => (
                                                            <li key={idx}>
                                                                {availableTables[mainTable]?.columns[f.column] || f.column} {f.operator} <b>{f.value}</b>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="results-table">
                                        {reportData.length > 0 ? (
                                            <table>
                                                <thead>
                                                    <tr>
                                                        {orderedColumnKeys.map(key => (
                                                            <th key={key}>{availableTables[mainTable]?.columns[key] || key}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.map((row, index) => (
                                                        <tr key={index}>
                                                            {orderedColumnKeys.map((key, i) => (
                                                                <td key={i}>
                                                                    {row[key] === null || row[key] === undefined ? '-' : String(row[key])}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="no-data">
                                                <p>No se encontraron datos para el reporte configurado.</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="step-actions">
                                        <button className="btn-back" onClick={() => setActiveStep(2)}>
                                            Atrás
                                        </button>
                                        <button className="btn-new-report" onClick={() => {
                                            setActiveStep(1);
                                            setSelectedTables([]);
                                            setSelectedColumns({});
                                            setFilters([]);
                                            setReportData([]);
                                        }}>
                                            Nuevo Reporte
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
} 